import React, { useEffect, useState } from "react";
import {
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Clock,
} from "lucide-react";

const API = "https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/attendance";

export default function Attendance() {
  const [archives, setArchives] = useState([]);

  useEffect(() => {
    fetch(`${API}/archives`)
      .then((r) => r.json())
      .then((data) => setArchives(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, []);

  return (
    <div className="p-6 md:p-10 space-y-8 bg-slate-950 min-h-screen text-slate-200">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Attendance Management
          </h1>
          <p className="text-slate-400 text-sm">
            Monthly overview & archives
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-900 px-3 py-1.5 rounded-full border border-slate-800">
          <Clock size={14} />
          {new Date().toLocaleDateString("en-IN")}
        </div>
      </div>

      <MonthlyAttendanceSection />

      <div className="pt-4 border-t border-slate-800">
        <h3 className="text-lg font-bold mb-4">Archived Reports</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {archives.map((a, i) => (
            <div
              key={i}
              onClick={() =>
                window.open(
                  `${API}/export-pdf?year=${a.year}&month=${a.month}`
                )
              }
              className="bg-slate-900 border border-slate-800 p-4 rounded-xl flex justify-between items-center cursor-pointer hover:border-indigo-500 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText size={18} />
                <div>
                  <div className="text-sm font-medium">
                    {new Date(a.year, a.month - 1).toLocaleDateString(
                      "en-US",
                      { month: "long", year: "numeric" }
                    )}
                  </div>
                  <div className="text-xs text-slate-500">PDF</div>
                </div>
              </div>
              <Download size={16} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MonthlyAttendanceSection() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [records, setRecords] = useState([]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    fetch(`${API}/monthly?year=${year}&month=${month + 1}`)
      .then((r) => r.json())
      .then((data) =>
        setRecords(Array.isArray(data.records) ? data.records : [])
      )
      .catch(console.error);
  }, [year, month]);

  const employees = {};
  records.forEach((r) => {
    if (!employees[r.email]) {
      employees[r.email] = {
        name: r.name,
        email: r.email,
        attendance: Array(daysInMonth).fill(0),
      };
    }
    const dayIndex = Number(r.date.split("-")[2]) - 1;
    employees[r.email].attendance[dayIndex] =
      r.status === "PRESENT" ? 1 : 0;
  });

  const empList = Object.values(employees);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
      <div className="p-5 flex justify-between items-center border-b border-slate-800">
        <div>
          <h2 className="font-bold text-white">Attendance Sheet</h2>
          <p className="text-xs text-slate-500">
            {currentDate.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() =>
              window.open(
                `${API}/export-pdf?year=${year}&month=${month + 1}`
              )
            }
            className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs"
          >
            <FileSpreadsheet size={14} /> Export Month PDF
          </button>

          <button
            onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            className="p-1.5 hover:bg-slate-800 rounded"
          >
            <ChevronLeft size={16} />
          </button>

          <button
            onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            className="p-1.5 hover:bg-slate-800 rounded"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-950 text-slate-400">
            <tr>
              <th className="p-3 text-left sticky left-0 bg-slate-900">
                Employee
              </th>
              {daysArray.map((d) => (
                <th key={d} className="p-2 text-center">
                  {d}
                </th>
              ))}
              <th className="p-3 text-center">Total</th>
              <th className="p-3 text-center">PDF</th>
            </tr>
          </thead>

          <tbody>
            {empList.map((emp, i) => (
              <tr key={i} className="border-t border-slate-800">
                <td className="p-3 sticky left-0 bg-slate-900">
                  {emp.name}
                </td>

                {emp.attendance.map((a, idx) => (
                  <td key={idx} className="text-center">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        a ? "bg-emerald-500" : "bg-rose-500"
                      }`}
                    />
                  </td>
                ))}

                <td className="text-center text-xs">
                  {emp.attendance.filter(Boolean).length}/{daysInMonth}
                </td>

                <td className="text-center">
                  <button
                    onClick={() =>
                      window.open(
                        `${API}/export-pdf?year=${year}&month=${month + 1}&email=${emp.email}`
                      )
                    }
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-800 hover:bg-indigo-600 rounded text-slate-300 hover:text-white transition-colors"
                  >
                    <Download size={12} /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {empList.length === 0 && (
          <div className="p-6 text-center text-slate-500">
            No attendance data for this month
          </div>
        )}
      </div>
    </div>
  );
}
